import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Box, Container } from "@mui/material";
import Header from "../components/Header";
import FiltersSidebar from "../components/FiltersSidebar";
import FlightResultsMain from "../components/FlightResultsMain";
import RightSidebar from "../components/RightSidebar";
import { useSearchContext } from "../context/SearchContext";
import { searchFlights } from "../utils/flightSearchUtils";
import { extractAvailableFilters, applyFilters } from "../utils/filterUtils";

function ResultPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  
  const { 
    from, setFrom,
    to, setTo,
    departureDate, setDepartureDate,
    returnDate, setReturnDate,
    tripType, setTripType,
    adults, setAdults,
    students, setStudents,
    seniors, setSeniors,
    youths, setYouths,
    childrenCount, setChildrenCount,
    toddlers, setToddlers,
    infants, setInfants,
    cabin, setCabin,
    // Filter states
    trackPrices, setTrackPrices,
    bookOnKayak, setBookOnKayak,
    timeFilter, setTimeFilter,
    timeRanges, setTimeRanges,
    durationRanges, setDurationRanges,
    smartFilterText, setSmartFilterText,
    carryOnCount, setCarryOnCount,
    checkedBagCount, setCheckedBagCount,
    stopsFilter, setStopsFilter,
    airlinesFilter, setAirlinesFilter,
    allianceFilter, setAllianceFilter,
    airportsFilter, setAirportsFilter,
    layoverAirportsFilter, setLayoverAirportsFilter,
    cabinFilter, setCabinFilter,
    priceRange, setPriceRange,
    amenitiesFilter, setAmenitiesFilter,
    flightQuality, setFlightQuality,
    // Search results
    searchResults, setSearchResults,
    filteredResults, setFilteredResults,
    availableFilters, setAvailableFilters,
  } = useSearchContext();

  // Function to perform flight search based on current search criteria
  const performFlightSearch = () => {
    setIsSearching(true);
    console.log("Performing flight search with criteria:", { from, to, departureDate, returnDate, tripType, cabin, adults });

    // Simulate API delay
    setTimeout(() => {
      try {
        // Use the new search function to get results from flights data
        const searchCriteria = {
          from,
          to,
          departureDate,
          returnDate,
          tripType,
          cabin,
          adults
        };

        const searchResults = searchFlights(searchCriteria);
        console.log("Search results:", searchResults);

        // Set search results and extract available filters
        setSearchResults(searchResults);
        const availableFilterOptions = extractAvailableFilters(searchResults);
        setAvailableFilters(availableFilterOptions);
        
        // Initially show all results (no filters applied)
        setFilteredResults(searchResults);
      } catch (error) {
        console.error("Error performing flight search:", error);
        setSearchResults([]);
        setFilteredResults([]);
        setAvailableFilters({
          airlines: [],
          stops: [],
          cabins: [],
          amenities: [],
          priceRange: { min: 0, max: 0 },
          stopsPrices: {}
        });
      } finally {
        setIsSearching(false);
      }
    }, 1000); // 1 second delay to simulate API call
  };

  // Handle search submission from the search bar
  const handleSearchSubmit = (searchData) => {
    console.log("Received search data:", searchData);
    
    // Update context with new search data
    if (searchData.from) setFrom(searchData.from);
    if (searchData.to) setTo(searchData.to);
    if (searchData.departureDate) setDepartureDate(searchData.departureDate);
    if (searchData.returnDate) setReturnDate(searchData.returnDate);
    if (searchData.tripType) setTripType(searchData.tripType);
    if (searchData.adults) setAdults(searchData.adults);
    if (searchData.cabin) setCabin(searchData.cabin);
    
    // Perform the search
    setTimeout(() => {
      performFlightSearch();
    }, 100);
  };

  // Update price range when available filters change
  useEffect(() => {
    if (availableFilters.priceRange && availableFilters.priceRange.max > 0) {
      setPriceRange(availableFilters.priceRange);
    }
  }, [availableFilters.priceRange]);

  // Apply filters whenever filter state changes
  useEffect(() => {
    if (searchResults.length > 0) {
      const filters = {
        stopsFilter,
        airlinesFilter,
        cabinFilter,
        amenitiesFilter,
        layoverAirportsFilter,
        priceRange: priceRange.min === 0 && priceRange.max === 10000 ? null : priceRange,
        timeRanges,
        timeFilter,
        durationRanges,
        flightQuality,
      };
      
      const filtered = applyFilters(searchResults, filters);
      setFilteredResults(filtered);
    }
  }, [searchResults, stopsFilter, airlinesFilter, cabinFilter, amenitiesFilter, layoverAirportsFilter, priceRange, timeRanges, timeFilter, durationRanges]);

  // Perform initial search when component mounts
  useEffect(() => {
    if (from.length > 0 && to.length > 0) {
      performFlightSearch();
    }
  }, []);

  // Use shared search state
  const searchData = {
    from,
    to,
    departureDate,
    returnDate,
    tripType,
    adults,
    students,
    seniors,
    youths,
    childrenCount,
    toddlers,
    infants,
    cabin,
  };

  const headerProps = {
    showSearchBar: true,
    searchBarProps: {
      initialFrom: searchData.from,
      initialTo: searchData.to,
      initialDepartureDate: searchData.departureDate,
      initialReturnDate: searchData.returnDate,
      initialTripType: searchData.tripType,
      initialAdults: searchData.adults,
      initialStudents: searchData.students,
      initialSeniors: searchData.seniors,
      initialYouths: searchData.youths,
      initialChildren: searchData.childrenCount,
      initialToddlers: searchData.toddlers,
      initialInfants: searchData.infants,
      initialCabin: searchData.cabin,
      isResultPage: true,
      onSwap: ({ from: newFrom, to: newTo }) => {
        setFrom(newFrom);
        setTo(newTo);
      },
      onFieldClick: (field) => {
        // Handle field click if needed
      },
      onSubmit: handleSearchSubmit,
      buttonLabel: "Update",
    },
    sidebarOpen,
    setSidebarOpen,
  };

  // Sidebar width: 220px when open, 56px when closed
  const sidebarWidth = sidebarOpen ? 220 : 56;
  const contentPaddingLeft = 24;

  return (
    <Box sx={{ background: "#f3f5f6", minHeight: "100vh" }}>
      <Header {...headerProps} isResultPage={true} />
      <Container
        maxWidth={false}
        sx={{
          display: "flex",
          maxWidth: 1800,
          margin: "0 auto",
          padding: "24px 0",
          paddingLeft: "100px", // Match header's search bar alignment
          transition: "padding-left 0.2s",
        }}
      >
        {/* Filters Sidebar */}
        <FiltersSidebar />

        {/* Main Content */}
        <FlightResultsMain 
          searchResults={filteredResults}
          isSearching={isSearching}
          totalResults={searchResults.length}
          tripType={tripType}
        />

        {/* Right Sidebar */}
        <RightSidebar />
      </Container>
    </Box>
  );
}

export default ResultPage;
